import React, { useState, useMemo, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, Tag, ShoppingCart, LogOut, Plus, ChevronsUpDown, Building2, Menu, Settings, Wallet, FileBarChart, Receipt, Target, PieChart, Users, CreditCard, History, ShieldAlert,
} from "lucide-react";
import { TriaLogo, TriaFooter } from "@/components/Brand";

const navMain = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/lotes", label: "Lotes", icon: Package, testid: "nav-lotes" },
  { to: "/mercadorias", label: "Mercadorias", icon: Tag, testid: "nav-mercadorias" },
  { to: "/vendas", label: "Vendas", icon: ShoppingCart, testid: "nav-vendas" },
];
const navFin = [
  { to: "/financeiro", label: "Financeiro", icon: Wallet, testid: "nav-financeiro" },
  { to: "/dre", label: "DRE Gerencial", icon: FileBarChart, testid: "nav-dre" },
  { to: "/despesas", label: "Despesas", icon: Receipt, testid: "nav-despesas" },
  { to: "/analises", label: "Análises", icon: PieChart, testid: "nav-analises" },
  { to: "/metas", label: "Metas & Cenários", icon: Target, testid: "nav-metas" },
];
const navBottom = [
  { to: "/membros", label: "Usuários", icon: Users, testid: "nav-membros" },
  { to: "/auditoria", label: "Dados & Auditoria", icon: History, testid: "nav-auditoria" },
  { to: "/plano", label: "Plano e Assinatura", icon: CreditCard, testid: "nav-plano" },
  { to: "/configuracoes", label: "Configurações", icon: Settings, testid: "nav-configuracoes" },
];

function BillingBanner() {
  const [sub, setSub] = useState(null);
  React.useEffect(() => {
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, []);
  if (!sub) return null;
  const blocked = ["EXPIRED", "CANCELLED", "SUSPENDED"].includes(sub.status);
  const trialEnding = sub.status === "TRIALING" && sub.trial_days_left <= 3;
  const pastDue = sub.status === "PAST_DUE";
  if (!blocked && !trialEnding && !pastDue) return null;
  const tone = blocked ? "bg-red-600" : pastDue ? "bg-orange-500" : "bg-amber-500";
  const msg = blocked
    ? "Seu período de teste terminou. A criação de novos dados está bloqueada — seus dados continuam acessíveis."
    : pastDue
    ? "Há um problema com o pagamento da sua assinatura. Regularize para evitar interrupções."
    : `Seu período de teste termina em ${sub.trial_days_left} dia(s). Escolha um plano para continuar.`;
  return (
    <div className={`${tone} text-white px-4 py-2.5 text-sm flex items-center justify-between gap-3`} data-testid="billing-banner">
      <span>{msg}</span>
      <a href="/plano" className="underline font-semibold whitespace-nowrap">Ver Plano e Assinatura</a>
    </div>
  );
}

function OrgSwitcher() {
  const { orgs, currentOrg, switchOrg, refreshOrgs } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/organizations", { name });
      await refreshOrgs();
      switchOrg(data);
      setOpen(false);
      setName("");
      toast.success("Organização criada");
      window.location.reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Select
        value={currentOrg?.id || ""}
        onValueChange={(id) => {
          const o = orgs.find((x) => x.id === id);
          if (o) { switchOrg(o); window.location.reload(); }
        }}
      >
        <SelectTrigger className="w-[190px] h-9" data-testid="org-selector">
          <Building2 className="h-4 w-4 mr-1 text-muted-foreground" />
          <SelectValue placeholder="Organização" />
        </SelectTrigger>
        <SelectContent>
          {orgs.map((o) => (
            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="icon" className="h-9 w-9" data-testid="org-create-button">
            <Plus className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Nova organização</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Brechó Central" data-testid="org-name-input" />
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={saving} data-testid="org-save-button">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function hexToHsl(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  let r = parseInt(m[1], 16) / 255, g = parseInt(m[2], 16) / 255, b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function OrgLogo({ org, compact }) {
  const [err, setErr] = useState(false);
  if (org?.has_logo && !err) {
    return (
      <img
        src={`${BACKEND}/api/organizations/${org.id}/logo?v=${org.logo_updated_at || ""}`}
        alt={org?.name || "Logo"}
        onError={() => setErr(true)}
        className={compact ? "h-8 max-w-[120px] object-contain" : "h-9 max-w-[160px] object-contain"}
        data-testid="org-logo"
      />
    );
  }
  if (compact) return <TriaLogo className="h-8 w-8" />;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <TriaLogo className="h-8 w-8 shrink-0" />
      <span className="font-display text-lg font-extrabold truncate max-w-[150px]">{org?.name || "TRIA"}</span>
    </div>
  );
}

export default function Layout() {
  const { user, logout, currentOrg } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const canFinance = ["OWNER", "ADMIN", "FINANCE", "MANAGER"].includes(currentOrg?.role);
  const canUsers = ["OWNER", "ADMIN", "FINANCE"].includes(currentOrg?.role);
  const canPlatform = !!user?.is_platform_admin;

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const bottomItems = useMemo(
    () => navBottom.filter((n) => (n.to !== "/membros" && n.to !== "/auditoria") || canUsers),
    [canUsers],
  );

  useEffect(() => {
    const root = document.documentElement;
    const hsl = hexToHsl(currentOrg?.brand_color);
    const keys = ["--accent", "--primary", "--ring"];
    if (hsl) keys.forEach((k) => root.style.setProperty(k, hsl));
    else keys.forEach((k) => root.style.removeProperty(k));
    return () => keys.forEach((k) => root.style.removeProperty(k));
  }, [currentOrg?.brand_color]);

  const NavItems = ({ onClick }) => {
    const link = (n) => (
      <NavLink
        key={n.to}
        to={n.to}
        end={n.end}
        onClick={onClick}
        data-testid={n.testid}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
          }`
        }
      >
        <n.icon className="h-4 w-4" />
        {n.label}
      </NavLink>
    );
    return (
      <nav className="flex flex-col gap-1">
        {navMain.map(link)}
        {canFinance && <p className="section-label px-3 pt-4 pb-1">Financeiro</p>}
        {canFinance && navFin.map(link)}
        <div className="my-2 h-px bg-border" />
        {bottomItems.map(link)}
        {canPlatform && link({ to: "/platform", label: "Admin da Plataforma", icon: ShieldAlert, testid: "nav-platform" })}
      </nav>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 flex-col border-r border-border bg-card px-4 py-6">
        <div className="flex items-center px-2 mb-8 h-10" data-testid="sidebar-brand">
          <OrgLogo org={currentOrg} />
        </div>
        <NavItems />
      </aside>

      {/* Main */}
      <div className="lg:pl-60">
        {currentOrg?.brand_color && <div className="h-1 w-full" style={{ background: currentOrg.brand_color }} data-testid="brand-strip" />}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-border bg-card/80 backdrop-blur px-4 sm:px-6 h-16">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
              <Menu className="h-5 w-5" />
            </Button>
            <div className="lg:hidden flex items-center" data-testid="mobile-brand"><OrgLogo org={currentOrg} compact /></div>
            <OrgSwitcher />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="user-menu">
                <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                  {(user?.name || "U").slice(0, 1).toUpperCase()}
                </div>
                <span className="hidden sm:inline text-sm max-w-[140px] truncate">{user?.name}</span>
                <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} data-testid="logout-button">
                <LogOut className="h-4 w-4 mr-2" /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {mobileOpen && (
          <div className="lg:hidden border-b border-border bg-card px-4 py-3" onClick={() => setMobileOpen(false)}>
            <NavItems onClick={() => setMobileOpen(false)} />
          </div>
        )}

        <BillingBanner />
        <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
          <Outlet />
        </main>
        <TriaFooter />
      </div>
    </div>
  );
}
